// Case9Page.js – AquaLine Water Consumption Challenge
import React, { useEffect, useState, useRef } from 'react';
import { PencilIcon, BoltIcon, ChartBarIcon, LightBulbIcon } from '@heroicons/react/24/outline';
const case9Image = require('../assets/RedRockCases/Case9/case 9 placeholder.png');
import exhibit1 from '../assets/RedRockCases/Case9/case9_ex1_usage_by_zone.png';
import exhibit2 from '../assets/RedRockCases/Case9/case9_ex2_reduction_income.png';
import SEO from '../components/SEO';

const TIMER_WARNING_THRESHOLD = 0.8; // 80% of the way through

const Case9Page = () => {
  const [hasStarted, setHasStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(1200);
  const [answers, setAnswers] = useState({});
  const [feedback, setFeedback] = useState({});
  const [reveal, setReveal] = useState({});
  const [timeExpired, setTimeExpired] = useState(false);
  const [finishedEarly, setFinishedEarly] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [notebookItems, setNotebookItems] = useState([]);
  const [isNotebookOpen, setIsNotebookOpen] = useState(true);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [currentExhibit, setCurrentExhibit] = useState('1');
  const timerRef = useRef(null);

  const questionData = [
    { 
      id: 1, 
      type: 'text', 
      question: 'According to Exhibit 1, which zone had the lowest average daily water use during the 90-day period?', 
      correctAnswer: 'Uptown' 
    },
    { 
      id: 2, 
      type: 'text',
      question: 'What was the percentage reduction in water use for Southgate from the start to the end of the program?',
      correctAnswer: '26.7%'
    },
    { 
      id: 3, 
      type: 'text', 
      question: 'Which income group achieved the highest reduction in water use?',
      correctAnswer: 'Low-Income' 
    },
    { 
      id: 4, 
      type: 'text', 
      question: 'What was the average leak response time in Eastline?', 
      correctAnswer: '9.2 hours' 
    },
    { 
      id: 5, 
      type: 'multi',
      question: 'Which two zones had the highest weekend-over-weekday water usage difference?',
      parts: [
        { label: 'First Zone', correctAnswer: 'Central' },
        { label: 'Second Zone', correctAnswer: 'Westfield' }
      ]
    },
    { 
      id: 6, 
      type: 'multi',
      question: 'What percentage of users were aware of billing changes, and what percentage understood tiered pricing?',
      parts: [
        { label: 'Aware of Billing Changes', correctAnswer: '73%' },
        { label: 'Understood Tiered Pricing', correctAnswer: '41%' }
      ]
    },
    { 
      id: 7, 
      type: 'text',
      question: 'What factor was ruled out as influencing the observed reductions across zones?',
      correctAnswer: 'Rainfall'
    },
    { 
      id: 8, 
      type: 'text', 
      question: 'Based on Exhibit 2, how much did middle-income households reduce their usage by?', 
      correctAnswer: '17%' 
    }
  ];

  // Type B: Move reportAnswers and dynamic scorecard variables to top-level scope
  const reportAnswers = {
    report_liters_day: '115', // Uptown daily usage (L/day)
    report_reduction: '26.7%', // Southgate reduction
    report_income_group: 'Low-Income', // Income group with highest reduction
    report_pricing_understanding: '41%', // % understood tiered pricing
    report_leak_zone: 'Eastline', // Highest leaks
    report_leak_count: '61', // Number of leaks in Eastline
    report_response_time: '9.2 hours' // Eastline response time
  };
  const numReportQuestions = Object.keys(reportAnswers).length;
  const numAnalysisQuestions = questionData.length;
  const numReportCorrect = Object.entries(feedback).filter(([k, v]) => v === 'correct' && k.startsWith('report_')).length;
  const numAnalysisCorrect = Object.entries(feedback).filter(([k, v]) => v === 'correct' && !k.startsWith('report_')).length;
  const totalQuestions = numReportQuestions + numAnalysisQuestions;
  const totalCorrect = numReportCorrect + numAnalysisCorrect;

  useEffect(() => {
    if (!hasStarted || timeExpired || finishedEarly) return;
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setTimeExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [hasStarted, timeExpired, finishedEarly]);

  useEffect(() => {
    // Check if all questions are answered
    const allAnswered = hasStarted && !isComplete && Object.keys(feedback).length === 8;
    
    if (allAnswered) {
      setIsComplete(true);
      // If completed and time is still left, mark as finished early
      if (timeLeft > 0) {
        setFinishedEarly(true);
        // Optional: Stop the timer if finished early
        // clearInterval(timerRef.current);
      }
    }
    // Added questionData.length, timeLeft, and setFinishedEarly to dependencies
  }, [feedback, hasStarted, isComplete, timeLeft, setFinishedEarly]);

  const handleInput = (id, value, partIndex = null) => {
    if (timeExpired || !hasStarted || finishedEarly) return;
    if (partIndex !== null) {
      setAnswers((prev) => ({
        ...prev,
        [id]: { ...(prev[id] || {}), [partIndex]: value }
      }));
    } else {
    setAnswers((prev) => ({ ...prev, [id]: value }));
    }
  };

  const handleSubmit = (id) => {
    if (timeExpired || !hasStarted || finishedEarly) return;
    const q = questionData.find((q) => q.id === id);
    
    if (q.type === 'multi') {
      const allPartsCorrect = q.parts.every((part, index) => {
        const userAnswer = ((answers[id] || {})[index] || '').trim().toLowerCase();
        return userAnswer === part.correctAnswer.toLowerCase();
      });
      setFeedback((prev) => ({ ...prev, [id]: allPartsCorrect ? 'correct' : 'incorrect' }));
    } else {
    const userAnswer = (answers[id] || '').trim().toLowerCase();
    const correctAnswer = q.correctAnswer.toLowerCase();
    setFeedback((prev) => ({ ...prev, [id]: userAnswer === correctAnswer ? 'correct' : 'incorrect' }));
    }
  };

  const handleTryAgain = (id) => {
    if (timeExpired || !hasStarted || finishedEarly) return;
    setAnswers((prev) => ({ ...prev, [id]: '' }));
    setFeedback((prev) => ({ ...prev, [id]: null }));
    setReveal((prev) => ({ ...prev, [id]: false }));
  };

  const handleReveal = (id) => {
    setReveal((prev) => ({ ...prev, [id]: true }));
  };

  const handleMCQSelect = (id, optionLabel) => {
    if (timeExpired || !hasStarted || finishedEarly) return;
    setAnswers((prev) => ({ ...prev, [id]: optionLabel }));
    const q = questionData.find((q) => q.id === id);
    setFeedback((prev) => ({ ...prev, [id]: optionLabel === q.correctAnswer ? 'correct' : 'incorrect' }));
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleReportSubmit = () => {
    if (timeExpired || !hasStarted || finishedEarly) return;

    const newFeedback = {};
    Object.entries(reportAnswers).forEach(([key, correctAnswer]) => {
      const userAnswer = (answers[key] || '').trim();
      newFeedback[key] = userAnswer.toLowerCase() === correctAnswer.toLowerCase() ? 'correct' : 'incorrect';
    });

    setFeedback(prev => ({ ...prev, ...newFeedback, report: 'submitted' }));
  };

  const handleReportReveal = () => {
    setReveal(prev => ({ ...prev, report: true }));
  };

  const handleDragStart = (e, value, label) => {
    if (e.target.closest('.notebook-content')) return;
    const data = {
      value: value,
      label: label
    };
    e.dataTransfer.setData('application/json', JSON.stringify(data));
  };

  const handleDrop = (e) => {
    if (!hasStarted) return;
    e.preventDefault();
    try {
      const data = e.dataTransfer.getData('application/json');
      if (!data) return;
      
      const parsedData = JSON.parse(data);
      if (parsedData.value && parsedData.label) {
        setNotebookItems(prev => [...prev, parsedData]);
      }
    } catch (error) {
      console.error('Error handling drop:', error);
    }
    e.currentTarget.classList.remove('bg-blue-50');
  };

  const removeNotebookItem = (index) => {
    setNotebookItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleNotebookToggle = () => {
    if (!isNotebookOpen) {
      setHasBeenOpened(true);
    }
    setIsNotebookOpen(!isNotebookOpen);
  };

  return (
    <div className="bg-white text-gray-900 font-sans relative scroll-pt-[400px] scroll-smooth">
      <SEO noIndex={true} />
      {/* Side Navigation */}
      <div className="fixed right-6 top-[285px] flex flex-col gap-6 z-[9999]">
        <div className="group relative">
        <a 
          href="#problem" 
            className="flex items-center"
          aria-label="Problem Section"
        >
            <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
            Problem
          </div>
          <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
            <PencilIcon className="w-5 h-5 text-gray-400" />
          </div>
        </a>
        </div>

        <div className="group relative">
          <a 
            href="#investigation" 
            className="flex items-center"
            aria-label="Investigation Section"
          >
            <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
              Investigation
          </div>
          <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
            <BoltIcon className="w-5 h-5 text-gray-400" />
          </div>
        </a>
        </div>

        <div className="group relative">
          <a 
            href="#exhibits" 
            className="flex items-center"
            aria-label="Exhibits Section"
          >
            <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
              Exhibits
            </div>
            <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </a>
        </div>

        <div className="group relative">
          <a 
            href="#questions" 
            className="flex items-center"
            aria-label="Analysis Section"
          >
            <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
              Analysis
          </div>
          <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
            <ChartBarIcon className="w-5 h-5 text-gray-400" />
          </div>
        </a>
        </div>

        <div className="group relative">
        <a 
          href="#recommendation" 
            className="flex items-center"
          aria-label="Recommendation Section"
        >
            <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
            Recommendation
          </div>
          <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
            <LightBulbIcon className="w-5 h-5 text-gray-400" />
          </div>
        </a>
        </div>
      </div>

      {/* Hero Section - Enhanced gradient */}
      <section className="sticky top-[50px] z-30 bg-gradient-to-br from-blue-200 to-white pb-4 relative">
        <div className="container pl-[72px] grid grid-cols-1 md:grid-cols-2 gap-8 pt-16">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-3">Case 9: AquaLine Water Conservation Program Analysis</h1>
            <p className="text-base md:text-lg text-gray-700">Analyze AquaLine's water conservation initiatives across different neighborhoods and demographics to determine which approaches had the greatest impact and should be scaled city-wide.</p>
            <div className="mt-4 mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-sm text-blue-700 flex items-center gap-2">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                For the best experience, adjust your browser zoom to 75-80%. This ensures all case elements display properly and are easy to interact with.
              </p>
            </div>
            {(!hasStarted || timeExpired || finishedEarly) && (
              <button
                onClick={() => {
                  setHasStarted(true);
                  setTimeLeft(1200);
                  setAnswers({});
                  setFeedback({});
                  setReveal({});
                  setTimeExpired(false);
                  setFinishedEarly(false);
                  setIsComplete(false);
                }}
                className="group relative mt-4 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
              >
                {!hasStarted ? 'Start Case' : 'Restart Case'}
                <div className="absolute top-full mt-2 left-0 bg-gray-800 text-white text-xs px-3 py-2 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-auto">
                  {!hasStarted ? 'Click to unlock the questions and start your case timer.' : 'Click to start a new attempt.'}
                </div>
              </button>
            )}
          </div>
        </div>
        <div className="absolute top-[85px] right-10 md:right-20 w-[300px] h-[400px] z-10 hidden md:block">
          {hasStarted && (timeExpired || isComplete) && (
            <div className={`absolute top-0 -left-[250px] bg-white/90 backdrop-blur-sm rounded-lg p-4 animate-pulse w-[220px] ${
              Math.round(((Object.values(feedback).filter(f => f === 'correct').length) / (totalQuestions)) * 100) < 65 
              ? 'shadow-[0_0_15px_rgba(239,68,68,0.2)]' 
              : 'shadow-[0_0_15px_rgba(59,130,246,0.2)]'
            }`}>
              <div className="relative">
                <div className={`absolute inset-0 bg-gradient-to-r rounded-lg ${
                  Math.round(((Object.values(feedback).filter(f => f === 'correct').length) / (totalQuestions)) * 100) < 65
                  ? 'from-red-50/50 to-white/50'
                  : 'from-blue-50/50 to-white/50'
                }`}></div>
                <div className="relative space-y-2.5">
                  <h2 className="text-[16px] font-semibold text-gray-800 flex items-center gap-1.5">
                    <span className="text-lg">📊</span> Final Score
                  </h2>
                  <p className="text-[15px] text-gray-700">
                    Analysis: {numAnalysisCorrect}/{numAnalysisQuestions}
                  </p>
                  <p className="text-[15px] text-gray-700">
                    Report: {numReportCorrect}/{numReportQuestions}
                  </p>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div 
                      className={`rounded-full h-2 transition-all duration-300 ${
                        Math.round(((Object.values(feedback).filter(f => f === 'correct').length) / (totalQuestions)) * 100) < 65
                        ? 'bg-red-500'
                        : 'bg-blue-500'
                      }`}
                      style={{ width: `${Math.round(((Object.values(feedback).filter(f => f === 'correct').length) / (totalQuestions)) * 100)}%` }}
                    ></div>
                  </div>
                  <p className={`text-[14px] ${
                    Math.round(((Object.values(feedback).filter(f => f === 'correct').length) / (totalQuestions)) * 100) < 65
                    ? 'text-red-600'
                    : 'text-gray-600'
                  }`}>
                    Total: {totalCorrect}/{totalQuestions} ({Math.round(((Object.values(feedback).filter(f => f === 'correct').length) / (totalQuestions)) * 100)}% Accuracy)
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="h-full bg-gray-300 rounded-xl flex items-center justify-center text-gray-500 shadow-md">
            <img 
              src={case9Image} 
              alt="AquaLine Water Conservation Case" 
              className="w-full h-full object-cover rounded-xl"
            />
          </div>
          {isNotebookOpen && hasBeenOpened && (
            <div className="w-[300px] bg-white rounded-xl shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] overflow-hidden transition-all duration-300 mt-4">
              <div className="p-3 border-b border-gray-100">
                <div className="flex justify-between items-start mb-1">
                  <div>
                    <h3 className="text-base font-medium text-gray-700">Research Notes</h3>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      💡 Tip: Click power to collapse notebook & click floating to reopen the notebook wherever you are in the case
                    </p>
                  </div>
                  <button 
                    className={`text-gray-400 transition-colors duration-300 group relative p-1 ${
                      hasStarted ? 'hover:text-blue-600' : 'opacity-50 cursor-not-allowed'
                    }`}
                    onClick={() => hasStarted && handleNotebookToggle()}
                    disabled={!hasStarted}
                    title={hasStarted ? "Click to collapse notebook" : "Start case to enable notebook controls"}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v6m0 6v6M5.636 5.636l4.243 4.243m4.242 4.242l4.243 4.243M3 12h6m6 0h6M5.636 18.364l4.243-4.243m4.242-4.242l4.243-4.243" />
                    </svg>
                    {hasStarted && (
                      <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 pointer-events-none">
                        <div className="bg-gray-800/80 backdrop-blur-sm text-white text-[11px] rounded-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap shadow-lg">
                          Click to collapse notebook
                        </div>
                      </div>
                    )}
                  </button>
                </div>
              </div>
              <div 
                className="notebook-content h-[300px] bg-gray-50 p-3 overflow-y-auto shadow-[inset_0_2px_4px_rgba(0,0,0,0.06)]"
                onDragOver={(e) => {
                  if (!hasStarted) return;
                  e.preventDefault();
                  e.currentTarget.classList.add('bg-blue-50');
                }}
                onDragLeave={(e) => {
                  if (!hasStarted) return;
                  e.preventDefault();
                  e.currentTarget.classList.remove('bg-blue-50');
                }}
                onDrop={handleDrop}
                contentEditable={hasStarted}
                suppressContentEditableWarning={true}
                style={{ outline: 'none', cursor: hasStarted ? 'text' : 'not-allowed' }}
              >
                {!hasStarted && (
                  <div className="text-gray-500 text-sm italic">
                    Click "Start Case" to begin taking notes...
                  </div>
                )}
                {notebookItems.map((item, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-2 bg-white p-2 rounded shadow-[inset_0_1px_2px_rgba(0,0,0,0.05)] group mb-2"
                    contentEditable={false}
                  >
                    <div className="flex-1">
                      <div className="text-xs font-medium text-gray-900">{item.value}</div>
                      <div className="text-[10px] text-gray-500 mt-0.5">{item.label}</div>
                    </div>
                    <button
                      onClick={() => hasStarted && removeNotebookItem(index)}
                      className={`${hasStarted ? 'opacity-0 group-hover:opacity-100' : 'hidden'} transition-opacity text-gray-400 hover:text-red-500`}
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {hasStarted && (
          <div className="fixed top-[85px] right-10 md:right-20 z-50">
            <div 
              className={`bg-white text-[15px] font-medium px-3 py-2 rounded-lg shadow-md flex items-center gap-1.5 ${
                timeLeft <= (1200 * (1 - TIMER_WARNING_THRESHOLD)) && timeLeft > 0
                  ? 'animate-pulse-dramatic border-2 border-red-500'
                  : ''
              }`}
            >
              <span className={`text-base ${
                timeLeft <= (1200 * (1 - TIMER_WARNING_THRESHOLD)) && timeLeft > 0
                  ? 'animate-bounce'
                  : ''
              }`}>⌛</span>
              <span className={`${
                timeLeft <= (1200 * (1 - TIMER_WARNING_THRESHOLD)) && timeLeft > 0
                  ? 'text-red-500 font-bold'
                  : 'text-blue-600'
              }`}>
                Time Left: {formatTime(timeLeft)}
              </span>
            </div>
          </div>
        )}
      </section>

      {/* Problem Section - Enhanced design */}
      <div id="problem" className="block h-0 -mt-[400px] pt-[400px]"></div>
      <section className="pt-20 pb-24 bg-gradient-to-br from-blue-100/60 via-blue-50/40 to-white relative">
        <div className="absolute inset-x-0 -top-20 h-20 bg-white"></div>
        <div className="container pl-[72px] grid grid-cols-1 md:grid-cols-2 gap-10">
          <div className="md:col-span-2 relative group" style={{ 
            paddingRight: 'calc(460px + 1.25rem)',
            maxWidth: 'calc(100% - 100px)'
          }}>
            <div className="absolute -top-20 inset-x-0 bottom-0 bg-white rounded-2xl rounded-t-none shadow-xl transform group-hover:scale-[1.02] transition-transform duration-300">
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-50/30 to-blue-50/50 opacity-50" 
                   style={{ 
                     clipPath: 'polygon(0 25%, 100% 25%, 100% 100%, 0 100%)',
                     background: 'linear-gradient(to bottom, transparent 0%, rgba(59, 130, 246, 0.05) 50%, rgba(59, 130, 246, 0.1) 100%)'
                   }}>
              </div>
            </div>
            <div className="relative p-8">
              <h2 className="text-3xl font-bold mb-6 flex items-center gap-3 text-gray-900">
                <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
                  📝
                </span>
                Problem
              </h2>
              <div className="space-y-4 prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed">AquaLine is the regional water authority that recently implemented a comprehensive water conservation program across five city zones. Following drought warnings and increasing environmental concerns, AquaLine launched this 90-day pilot initiative featuring smart water meters, a tiered pricing structure that increases costs for excessive usage, automated leak detection systems, and targeted educational outreach campaigns.</p>
                <p className="text-gray-700 leading-relaxed">Your consulting team has been tasked with analyzing the program's effectiveness across different neighborhoods and demographics. This analysis will determine not just where overall usage decreased, but also identify which specific interventions were most successful, where infrastructure improvements are needed, and which demographic groups responded most positively to the conservation efforts.</p>
                <p className="text-gray-700 leading-relaxed">The deliverable is a data-driven set of recommendations for AquaLine's leadership team as they plan the city-wide expansion of their water conservation strategy, with specific interventions tailored to each neighborhood's unique characteristics and needs.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Investigation Section */}
      <div id="investigation" className="block h-0 -mt-[400px] pt-[400px]"></div>
      <section className="py-12 bg-gray-50/80">
        <div className="container pl-[72px]">
          <h2 className="text-3xl font-bold mb-3 flex items-center gap-3">
            <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
              🔍
            </span>
            Investigation
          </h2>
          <p className="text-sm text-gray-600 mb-8 flex items-center gap-2">
            <span className="bg-blue-50 px-2 py-0.5 rounded">💡 Tip:</span>
            Drag and drop highlighted text into your notebook to collect important data points
          </p>
          <div className="flex gap-12 pr-[200px]">
            {/* Investigation Text */}
            <div className="w-[700px]">
              <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group relative space-y-5">
                <p className="text-lg leading-relaxed">
                  During the 90-day pilot, AquaLine tracked consumption from over <span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "40,000", "Total households tracked")}
                  >40,000</span> households. Exhibit 1 shows per capita daily usage by zone: Uptown and Northside averaged the lowest (<span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "115", "Uptown daily usage (L/day)")}
                  >115</span> and <span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "123", "Northside daily usage (L/day)")}
                  >123</span> L/day), while Westfield peaked at <span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "167", "Westfield daily usage (L/day)")}
                  >167</span> L/day. Southgate's reduction was the most dramatic — from <span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "180", "Southgate initial usage (L/day)")}
                  >180</span> to <span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "132", "Southgate final usage (L/day)")}
                  >132</span> L/day.
                </p>

                <p className="text-lg leading-relaxed">
                  Zone-level smart meter data showed that areas with higher tiered billing adoption saw sharper usage drops. Exhibit 2 reveals that higher-income households reduced consumption less (<span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "8%", "Higher-income reduction")}
                  >8%</span>) than middle-income (<span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "17%", "Middle-income reduction")}
                  >17%</span>) and low-income (<span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "21%", "Low-income reduction")}
                  >21%</span>) brackets.
                </p>

                <p className="text-lg leading-relaxed">
                  Leak incidents were highest in Eastline (<span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "61", "Eastline leak incidents")}
                  >61</span>), but infrastructure response times there were fastest (avg. <span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "9.2 hours", "Eastline response time")}
                  >9.2 hours</span>). Customer survey data shows <span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "73%", "Noticed billing changes")}
                  >73%</span> of users noticed billing changes, but only <span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "41%", "Understood tiered pricing")}
                  >41%</span> understood tiered pricing fully.
                </p>

                <p className="text-lg leading-relaxed">
                  Weekend usage remained significantly higher than weekday usage in <span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "Central", "High weekend usage zone 1")}
                  >Central</span> and <span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "Westfield", "High weekend usage zone 2")}
                  >Westfield</span>, possibly due to outdoor irrigation and cleaning activities. Weather patterns were stable across zones, ruling out <span 
                    className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                    draggable={hasStarted}
                    onDragStart={(e) => hasStarted && handleDragStart(e, "rainfall", "Ruled out factor")}
                  >rainfall</span> as a confounding factor.
                </p>
              </div>
            </div>

            {/* Interactive Notebook */}
            {isNotebookOpen ? (
              <div className="w-[500px] bg-white rounded-xl shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] overflow-hidden transition-all duration-300">
                <div className="p-4 border-b border-gray-100">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="text-lg font-medium text-gray-700">Research Notes</h3>
                      <p className="text-xs text-gray-500 mt-1">
                        💡 Tip: Click power to collapse notebook & click floating button to reopen the notebook wherever you are in the case
                      </p>
                    </div>
                    <button 
                      className={`text-gray-400 transition-colors duration-300 group relative p-1 ${
                        hasStarted ? 'hover:text-blue-600' : 'opacity-50 cursor-not-allowed'
                      }`}
                      onClick={() => hasStarted && handleNotebookToggle()}
                      disabled={!hasStarted}
                      title={hasStarted ? "Click to collapse notebook" : "Start case to enable notebook controls"}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v6m0 6v6M5.636 5.636l4.243 4.243m4.242 4.242l4.243 4.243M3 12h6m6 0h6M5.636 18.364l4.243-4.243m4.242-4.242l4.243-4.243" />
                      </svg>
                      {hasStarted && (
                        <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 pointer-events-none">
                          <div className="bg-gray-800/80 backdrop-blur-sm text-white text-[11px] rounded-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap shadow-lg">
                            Click to collapse notebook
                          </div>
                        </div>
                      )}
                    </button>
                  </div>
                </div>
                <div 
                  className="notebook-content h-[400px] bg-gray-50 p-4 overflow-y-auto shadow-[inset_0_2px_4px_rgba(0,0,0,0.06)]"
                  onDragOver={(e) => {
                    if (!hasStarted) return;
                    e.preventDefault();
                    e.currentTarget.classList.add('bg-blue-50');
                  }}
                  onDragLeave={(e) => {
                    if (!hasStarted) return;
                    e.preventDefault();
                    e.currentTarget.classList.remove('bg-blue-50');
                  }}
                  onDrop={handleDrop}
                  contentEditable={hasStarted}
                  suppressContentEditableWarning={true}
                  style={{ outline: 'none', cursor: hasStarted ? 'text' : 'not-allowed' }}
                >
                  {!hasStarted && (
                    <div className="text-gray-500 text-sm italic">
                      Click "Start Case" to begin taking notes...
                    </div>
                  )}
                  {notebookItems.map((item, index) => (
                    <div 
                      key={index}
                      className="flex items-start gap-2 bg-white p-2.5 rounded shadow-[inset_0_1px_2px_rgba(0,0,0,0.05)] group mb-2"
                      contentEditable={false}
                    >
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{item.value}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{item.label}</div>
                      </div>
                      <button
                        onClick={() => hasStarted && removeNotebookItem(index)}
                        className={`${hasStarted ? 'opacity-0 group-hover:opacity-100' : 'hidden'} transition-opacity text-gray-400 hover:text-red-500`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <button
                onClick={handleNotebookToggle}
                className={`fixed right-24 top-1/2 -translate-y-1/2 bg-white p-4 rounded-xl shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] transform transition-all duration-300 group z-[999] ${
                  hasStarted 
                    ? 'hover:shadow-[inset_0_2px_8px_rgba(0,0,0,0.15)] hover:scale-105'
                    : 'opacity-50 cursor-not-allowed'
                }`}
                disabled={!hasStarted}
              >
                <div className="relative">
                  {hasStarted && (
                    <div className="absolute -top-2 -right-2 w-4 h-4 bg-blue-500 rounded-full animate-pulse"></div>
                  )}
                  <svg 
                    className={`w-8 h-8 ${hasStarted ? 'text-gray-600 group-hover:text-blue-600' : 'text-gray-400'} transition-colors duration-300`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                {hasStarted && (
                  <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 pointer-events-none">
                    <div className="bg-gray-800/80 backdrop-blur-sm text-white text-sm rounded-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap shadow-lg">
                      Open notebook ({notebookItems.length} notes collected)
                    </div>
                  </div>
                )}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Exhibits Section */}
      <div id="exhibits" className="block h-0 -mt-[400px] pt-[400px]"></div>
      <section className="py-12 bg-gradient-to-br from-blue-100/60 via-blue-50/40 to-white relative">
        <div className="absolute inset-x-0 -top-20 h-20 bg-gray-50/80"></div>
        <div className="container pl-[72px]" style={{ 
            paddingRight: 'calc(460px + 1.25rem)',
            maxWidth: 'calc(100% - 100px)'
          }}>
          <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
            <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
              📈
            </span>
            Exhibits
          </h2>
          <p className="text-sm text-gray-600 mb-4">Select an exhibit using the navigation below to analyze the data.</p>
          
          {/* Exhibit Navigation */}
          <div className="flex items-center gap-3 mb-4">
            {['1', '2'].map((exhibit) => (
              <button
                key={exhibit}
                onClick={() => hasStarted && setCurrentExhibit(exhibit)}
                disabled={!hasStarted}
                className={`px-6 py-2 rounded-xl shadow-md transition-all duration-300 transform ${
                  !hasStarted 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                    : currentExhibit === exhibit
                      ? 'bg-gradient-to-r from-blue-600 to-blue-400 text-white hover:shadow-lg hover:scale-105'
                      : 'bg-white text-gray-600 hover:bg-blue-50 hover:shadow-lg hover:scale-105'
                }`}
              >
                {exhibit}
              </button>
            ))}
          </div>

          {/* Exhibit Display */}
          <div className={`bg-white rounded-xl shadow-sm transition-all duration-300 ${!hasStarted ? 'opacity-50' : 'hover:shadow-lg'}`}>
            <div className="p-4">
              {currentExhibit === '1' && (
                <>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="text-blue-600">📊</span>
                    Exhibit 1: Average Daily Water Use by Zone
                    {!hasStarted && (
                      <span className="text-xs text-gray-400 font-normal ml-2">(Start case to interact)</span>
                    )}
                  </h3>
                  <div className={`aspect-[21/9] bg-white rounded-lg flex items-center justify-center overflow-hidden ${!hasStarted ? 'filter blur-[2px]' : ''}`}>
                    <img 
                      src={exhibit1} 
                      alt="Water Usage by Zone" 
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Average daily water consumption (L/day) across urban zones during the 90-day conservation program.
                  </p>
                </>
              )}

              {currentExhibit === '2' && (
                <>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="text-blue-600">📈</span>
                    Exhibit 2: Water Usage Reduction by Income Group
                    {!hasStarted && (
                      <span className="text-xs text-gray-400 font-normal ml-2">(Start case to interact)</span>
                    )}
                  </h3>
                  <div className={`aspect-[21/9] bg-white rounded-lg flex items-center justify-center overflow-hidden ${!hasStarted ? 'filter blur-[2px]' : ''}`}>
                    <img 
                      src={exhibit2} 
                      alt="Usage Reduction by Income Group" 
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Percentage water usage reduction achieved by different income groups during the conservation program.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Analysis Section */}
      <div id="questions" className="block h-0 -mt-[400px] pt-[400px]"></div>
      <section className="py-12 bg-gray-50/80">
        <div className="container pl-[72px]">
          <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
            <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
              🧠
            </span>
            Analysis
          </h2>
          <div className="space-y-6 md:pr-[460px]">
            {questionData.map((q) => (
              <div 
                key={q.id} 
                className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group relative"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-blue-400 flex items-center justify-center text-white font-medium shadow-md">
                    {q.id}
                  </div>
                  <div className="flex-grow">
                    <h3 className="text-lg font-medium transition-all duration-300 hover:font-bold hover:text-blue-600 mb-4">
                      {q.question}
                    </h3>
                    {q.type === 'text' ? (
                      <div className="space-y-3">
                        <div className="flex gap-3">
                      <input
                        type="text"
                            value={answers[q.id] || ''}
                            onChange={(e) => handleInput(q.id, e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSubmit(q.id)}
                            className="flex-grow px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:border-blue-400"
                            placeholder="Type your answer and press Enter..."
                        disabled={!hasStarted || timeExpired || finishedEarly}
                      />
                          <button
                            onClick={() => handleSubmit(q.id)}
                            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                            disabled={!hasStarted || timeExpired || finishedEarly}
                          >
                            Submit
                          </button>
                        </div>
                        <div className="flex gap-2">
                          {feedback[q.id] === 'incorrect' && hasStarted && !timeExpired && !finishedEarly && (
                            <button
                              onClick={() => handleTryAgain(q.id)}
                              className="px-3 py-1.5 bg-gradient-to-r from-gray-600 to-gray-500 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                            >
                              Try Again
                            </button>
                          )}
                          {(timeExpired || finishedEarly) && (
                            <button
                              onClick={() => handleReveal(q.id)}
                              className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                            >
                              See Answer
                            </button>
                          )}
                        </div>
                        {feedback[q.id] && (
                          <div className={`mt-4 p-3 rounded-lg ${
                            feedback[q.id] === 'correct' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                          }`}>
                            {feedback[q.id] === 'correct' ? 'Correct!' : 'Incorrect. Try again.'}
                          </div>
                        )}
                        {reveal[q.id] && (
                          <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                            <p className="text-blue-700">🔍 Answer: <strong>{q.correctAnswer}</strong></p>
                          </div>
                        )}
                      </div>
                    ) : q.type === 'multi' ? (
                      <div className="space-y-3">
                        <div className="flex flex-col gap-3">
                          {q.parts.map((part, index) => (
                            <div key={index} className="flex gap-3">
                              <div className="flex-grow">
                                <label className="block text-sm font-medium text-gray-700 mb-1">{part.label}</label>
                                <input
                                  type="text"
                                  value={((answers[q.id] || {})[index] || '')}
                                  onChange={(e) => handleInput(q.id, e.target.value, index)}
                                  onKeyDown={(e) => e.key === 'Enter' && handleSubmit(q.id)}
                                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:border-blue-400"
                                  placeholder={`Enter ${part.label.toLowerCase()}...`}
                                  disabled={!hasStarted || timeExpired || finishedEarly}
                                />
                              </div>
                            </div>
                          ))}
                          <button
                            onClick={() => handleSubmit(q.id)}
                            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                            disabled={!hasStarted || timeExpired || finishedEarly}
                          >
                            Submit
                          </button>
                        </div>
                        <div className="flex gap-2">
                          {feedback[q.id] === 'incorrect' && hasStarted && !timeExpired && !finishedEarly && (
                            <button
                              onClick={() => handleTryAgain(q.id)}
                              className="px-3 py-1.5 bg-gradient-to-r from-gray-600 to-gray-500 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                            >
                              Try Again
                            </button>
                          )}
                          {(timeExpired || finishedEarly) && (
                            <button
                              onClick={() => handleReveal(q.id)}
                              className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                            >
                              See Answer
                            </button>
                          )}
                        </div>
                        {feedback[q.id] && (
                          <div className={`mt-4 p-3 rounded-lg ${
                            feedback[q.id] === 'correct' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                          }`}>
                            {feedback[q.id] === 'correct' ? 'Correct!' : 'Incorrect. Try again.'}
                          </div>
                        )}
                        {reveal[q.id] && (
                          <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                            <p className="text-blue-700">
                              🔍 Answer: 
                              {q.parts.map((part, index) => (
                                <span key={index}>
                                  <br />
                                  <strong>{part.label}</strong>: {part.correctAnswer}
                                </span>
                              ))}
                            </p>
                          </div>
                        )}
                      </div>
                    ) : q.type === 'mcq' ? (
                      <div className="space-y-2">
                        {q.options.map((option) => (
                          <button
                            key={option}
                            onClick={() => handleMCQSelect(q.id, option)}
                            className={`w-full text-left px-4 py-2 rounded-lg border transition-all duration-300 transform hover:scale-102 ${
                              answers[q.id] === option
                                ? 'bg-gradient-to-r from-blue-600 to-blue-400 text-white border-transparent'
                                : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                            }`}
                            disabled={!hasStarted || timeExpired || finishedEarly}
                          >
                            {option}
                          </button>
                        ))}
                        <div className="flex gap-2 mt-3">
                          {feedback[q.id] === 'incorrect' && hasStarted && !timeExpired && !finishedEarly && (
                            <button
                              onClick={() => handleTryAgain(q.id)}
                              className="px-3 py-1.5 bg-gradient-to-r from-gray-600 to-gray-500 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                            >
                              Try Again
                            </button>
                          )}
                          {(timeExpired || finishedEarly) && (
                            <button
                              onClick={() => handleReveal(q.id)}
                              className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                            >
                              See Answer
                            </button>
                          )}
                        </div>
                        {feedback[q.id] && (
                          <div className={`mt-4 p-3 rounded-lg ${
                            feedback[q.id] === 'correct' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                          }`}>
                            {feedback[q.id] === 'correct' ? 'Correct!' : 'Incorrect. Try again.'}
                        </div>
                      )}
                        {reveal[q.id] && (
                          <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                            <p className="text-blue-700">🔍 Answer: <strong>{q.correctAnswer}</strong></p>
                          </div>
                      )}
                    </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}

              {finishedEarly && (
                <div className="mt-6 text-green-600 text-base font-semibold">
                🎉 Congratulations! You completed all questions before the timer ran out! Amazing work!
                </div>
              )}
          </div>
        </div>
      </section>

      {/* Report Section */}
      <div id="report" className="block h-0 -mt-[400px] pt-[400px]"></div>
      <section className="py-20 bg-gradient-to-br from-blue-50/40 via-blue-50/20 to-white">
        <div className="container pl-[72px]">
          <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
            <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
              📊
            </span>
            Report
          </h2>
          <div className="space-y-6 md:pr-[460px]">
            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group relative">
              <div className="flex items-start gap-4">
                <div className="flex-grow">
                  <div className="space-y-3">
                    <div className="text-lg leading-relaxed text-justify" style={{ lineHeight: '2' }}>
                      Uptown demonstrated the best water conservation at <input
                        type="text"
                        value={answers['report_liters_day'] || ''}
                        onChange={(e) => handleInput('report_liters_day', e.target.value)}
                        className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                          (timeExpired || finishedEarly) && feedback['report_liters_day'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                          (timeExpired || finishedEarly) && feedback['report_liters_day'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                          'border-gray-300 focus:ring-blue-200'
                        }`}
                        disabled={!hasStarted || timeExpired || finishedEarly}
                        onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                      /> L/day. Southgate's 90-day reduction of <input
                        type="text"
                        value={answers['report_reduction'] || ''}
                        onChange={(e) => handleInput('report_reduction', e.target.value)}
                        className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                          (timeExpired || finishedEarly) && feedback['report_reduction'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                          (timeExpired || finishedEarly) && feedback['report_reduction'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                          'border-gray-300 focus:ring-blue-200'
                        }`}
                        disabled={!hasStarted || timeExpired || finishedEarly}
                        onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                      /> was the most substantial, reflecting a successful shift in user behavior.

                      Smart meters helped drive behavior change — especially in <input
                        type="text"
                        value={answers['report_income_group'] || ''}
                        onChange={(e) => handleInput('report_income_group', e.target.value)}
                        className={`w-32 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                          (timeExpired || finishedEarly) && feedback['report_income_group'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                          (timeExpired || finishedEarly) && feedback['report_income_group'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                          'border-gray-300 focus:ring-blue-200'
                        }`}
                        disabled={!hasStarted || timeExpired || finishedEarly}
                        onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                      /> income groups. However, understanding of pricing models remains low, with only <input
                        type="text"
                        value={answers['report_pricing_understanding'] || ''}
                        onChange={(e) => handleInput('report_pricing_understanding', e.target.value)}
                        className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                          (timeExpired || finishedEarly) && feedback['report_pricing_understanding'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                          (timeExpired || finishedEarly) && feedback['report_pricing_understanding'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                          'border-gray-300 focus:ring-blue-200'
                        }`}
                        disabled={!hasStarted || timeExpired || finishedEarly}
                        onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                      /> of users grasping tiered rates.

                      <input
                        type="text"
                        value={answers['report_leak_zone'] || ''}
                        onChange={(e) => handleInput('report_leak_zone', e.target.value)}
                        className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                          (timeExpired || finishedEarly) && feedback['report_leak_zone'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                          (timeExpired || finishedEarly) && feedback['report_leak_zone'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                          'border-gray-300 focus:ring-blue-200'
                        }`}
                        disabled={!hasStarted || timeExpired || finishedEarly}
                        onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                      /> had the highest number of reported leaks (<input
                        type="text"
                        value={answers['report_leak_count'] || ''}
                        onChange={(e) => handleInput('report_leak_count', e.target.value)}
                        className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                          (timeExpired || finishedEarly) && feedback['report_leak_count'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                          (timeExpired || finishedEarly) && feedback['report_leak_count'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                          'border-gray-300 focus:ring-blue-200'
                        }`}
                        disabled={!hasStarted || timeExpired || finishedEarly}
                        onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                      />), but the fastest response time (<input
                        type="text"
                        value={answers['report_response_time'] || ''}
                        onChange={(e) => handleInput('report_response_time', e.target.value)}
                        className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                          (timeExpired || finishedEarly) && feedback['report_response_time'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                          (timeExpired || finishedEarly) && feedback['report_response_time'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                          'border-gray-300 focus:ring-blue-200'
                        }`}
                        disabled={!hasStarted || timeExpired || finishedEarly}
                        onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                      /> hours), suggesting solid operational readiness.
                    </div>
                    <div className="flex gap-2">
                      {(!timeExpired && !finishedEarly) && (
                        <button
                          onClick={() => handleReportSubmit()}
                          className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                          disabled={!hasStarted || timeExpired || finishedEarly}
                        >
                          Submit Answers
                        </button>
                      )}
                      {(timeExpired || finishedEarly) && (
                        <button
                          onClick={() => handleReportReveal()}
                          className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                        >
                          See Answers
                        </button>
                      )}
                    </div>
                    {(timeExpired || finishedEarly) && feedback['report'] && (
                      <div className={`mt-4 p-3 rounded-lg ${
                        Object.values(feedback).every(f => f === 'correct') 
                          ? 'bg-green-50 text-green-700' 
                          : 'bg-red-50 text-red-700'
                      }`}>
                        {Object.values(feedback).every(f => f === 'correct') 
                          ? 'All answers are correct!' 
                          : 'Some answers are incorrect. Please check your answers.'}
                      </div>
                    )}
                    {reveal['report'] && (
                      <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                        <p className="text-blue-700">
                          🔍 Answers:<br/>
                          115 L/day consumption<br/>
                          26.7% reduction in Southgate<br/>
                          Low-Income (income group with highest reduction)<br/>
                          41% understood tiered pricing<br/>
                          Eastline (highest leaks)<br/>
                          61 reported leaks<br/>
                          9.2 hours response time
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Congratulatory and Time's Up Messages after the report section */}
            {finishedEarly && (
              <div className="mt-6 text-green-600 text-base font-semibold">
                🎉 Congratulations! You completed the report before the timer ran out! Amazing work!
              </div>
            )}
            {timeExpired && !finishedEarly && (
              <div className="mt-6 text-red-600 text-base font-semibold">
                ❌ Time's up! You can still view the answers by clicking "See Answers".
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Recommendation Section - Subtle off-white */}
      <div id="recommendation" className="block h-0 -mt-[400px] pt-[400px]"></div>
      <section className="py-20 bg-gray-50/80">
        <div className="container pl-[72px] grid grid-cols-1 md:grid-cols-2 gap-10">
          <div className="md:col-span-2 md:pr-[460px]">
            <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
                💡
              </span>
              Recommendation
            </h2>
            <ul className="space-y-4">
              <li className="flex items-start">
                <span className="mr-3 text-xl">📢</span>
                <span>Expand awareness programs to better explain pricing incentives</span>
              </li>
              <li className="flex items-start">
                <span className="mr-3 text-xl">🔧</span>
                <span>Prioritize upgrades in high-leak zones like Eastline</span>
              </li>
              <li className="flex items-start">
                <span className="mr-3 text-xl">📱</span>
                <span>Extend smart meter installation to remaining households, starting with low-income areas</span>
              </li>
              <li className="flex items-start">
                <span className="mr-3 text-xl">📅</span>
                <span>Use targeted weekend campaigns in Central and Westfield to lower peak water use</span>
              </li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Case9Page; 

<style jsx global>{`
    @keyframes pulse-dramatic {
      0%, 100% {
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
      }
      50% {
        transform: scale(1.15);
        box-shadow: 0 0 0 10px rgba(239, 68, 68, 0);
      }
    }
    
    .animate-pulse-dramatic {
      animation: pulse-dramatic 1s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    }

    @keyframes bounce {
      0%, 100% {
        transform: translateY(0) scale(1);
      }
      50% {
        transform: translateY(-5px) scale(1.2);
      }
    }

    .animate-bounce {
      animation: bounce 0.5s ease infinite;
    }
  `}</style>